import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';

/**
 * The free-text user filter (FR-008, R-014).
 *
 * Keystrokes mutate a *local* draft that is not part of the published scope;
 * only Enter and blur commit. Exact-match on a partial identifier would thrash
 * every subscribed view for nothing (2026-08-24 clarification), and making the
 * unchanged case an explicit no-op matters because `blur` fires on every focus
 * change — without it, tabbing through the bar would publish spurious updates
 * and break SC-006's exact-refresh counting.
 *
 * No list of users is offered: `groupBy=user` exists, so this is a deliberate
 * refusal to enumerate a PII-adjacent, high-cardinality dimension (spec C2),
 * not the absence of a means.
 */
@Component({
  selector: 'app-user-filter-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <label class="filter-label" for="filter-user-input">User</label>

    <input
      id="filter-user-input"
      type="text"
      class="user-input"
      data-testid="filter-user"
      autocomplete="off"
      spellcheck="false"
      placeholder="Any user"
      aria-describedby="filter-user-hint"
      [value]="draft()"
      (input)="onInput($event)"
      (keydown)="onKeydown($event)"
      (blur)="commitDraft()"
    />

    <p id="filter-user-hint" class="filter-hint" data-testid="filter-user-hint">
      Exact match. Press Enter to apply.
    </p>
  `,
  styles: `
    :host {
      display: block;
      min-width: 0;
    }

    .user-input {
      width: 100%;
      height: var(--input-height);
      padding: 0 var(--space-sm);
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-base);
      font-family: inherit;
      font-size: var(--font-size-sm);
      color: var(--color-text);

      &:focus {
        outline: none;
        border-color: var(--color-primary);
      }

      &::placeholder {
        color: var(--color-text-subtle);
      }
    }

    .filter-hint {
      // Reserved in every state so the bar's height never changes (SC-005).
      min-height: 1rem;
      margin: var(--space-3xs) 0 0;
      font-size: var(--font-size-xs);
      line-height: 1;
      color: var(--color-text-subtle);
    }
  `,
})
export class UserFilterInputComponent {
  readonly value = input<string | null>(null);

  /**
   * Bumped by the bar on Reset and on the FR-019 session clear. Writing the
   * default value alone cannot discard an uncommitted draft: with no committed
   * user, Reset rewrites `null` over `null`, which is no `value` change at all
   * (signals compare with `Object.is`), so the sync effect never runs and the
   * typed text stays on screen. That matters most here — this field holds an
   * identity-bearing value, and the same token fires on the session boundary,
   * where one owner's typing must not survive into the next owner's session.
   */
  readonly resetToken = input(0);

  /** Emitted on Enter or blur, and only when the committed value actually changed. */
  readonly commitValue = output<string | null>();

  private readonly local = signal<string | null>(null);

  /** The local draft while editing; the committed value otherwise. */
  protected readonly draft = computed(() => this.local() ?? this.value() ?? '');

  constructor() {
    // An external change — a Reset, or a URL restore — abandons any
    // uncommitted draft rather than silently keeping stale text on screen.
    effect(() => {
      this.value();
      untracked(() => this.local.set(null));
    });

    // An explicit Reset (or session clear) discards the draft even when the
    // committed value is unchanged — the effect above cannot: it only runs when
    // `value` actually changes. The initial run is skipped so a deep-linked
    // restore's state is not wiped at construction.
    let lastResetToken = untracked(() => this.resetToken());
    effect(() => {
      const token = this.resetToken();
      if (token === lastResetToken) return;
      lastResetToken = token;
      untracked(() => this.local.set(null));
    });
  }

  protected onInput(event: Event): void {
    this.local.set((event.target as HTMLInputElement).value);
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitDraft();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.local.set(null);
    }
  }

  protected commitDraft(): void {
    const local = this.local();
    if (local === null) return; // nothing was typed since the last commit

    const trimmed = local.trim();
    const committed = trimmed === '' ? null : trimmed;
    this.local.set(null);

    // R-014: committing an unchanged value publishes nothing.
    if (committed === this.value()) return;
    this.commitValue.emit(committed);
  }
}
